<script setup lang="ts">
import { computed, onBeforeUnmount, onMounted, ref, watch } from 'vue'
import { debounce } from 'lodash-es'
import { usePageContext } from '@/composables/usePageContext'
import { useScreen } from '@/composables/useScreen'
import { apiPost } from '@/lib/http'
import AppNavigation from '@/ui-kit/layout/AppNavigation.vue'
import { currentUser } from '@/lib/currentUser'
import type { MarketFiltersData, MarketProductCardItem, MarketSelectItem, MarketSelectValue } from '@/types/market'
import MarketFiltersMobile from '@/ui-kit/market/FiltersMobile.vue'
import MarketFiltersDesktop from '@/ui-kit/market/FiltersDesktop.vue'
import MarketFeaturedProducts from '@/ui-kit/market/FeaturedProducts.vue'
import ProductCard from '@/ui-kit/products/ProductCard.vue'

type KeyValItem = [MarketSelectValue, string]

interface PageContext {
  state: {
    languages: KeyValItem[]
    courses: KeyValItem[]
    contentTypes: KeyValItem[]
    holiday: KeyValItem[]
  }
  urls: {
    add_product: string
    my_products: string
    request_products: string
  }
}

const ctx = usePageContext<PageContext>()
const { isMobile } = useScreen()

const paginationItems: MarketSelectItem[] = [9, 30, 60].map(value => ({ value, text: String(value) }))

const page = ref(1)
const loadMoreCount = ref(0)
const total = ref(0)
const itemsOnPage = ref(30)
const filtersData = ref<MarketFiltersData>({
  min_price: '',
  max_price: '',
  search_text: '',
  language: '',
  owned: false,
  authored: false,
  available: false,
  courses: [],
  holiday: [],
  content_types: [],
  sort_order: '-creation_date'
})

const items = ref<MarketProductCardItem[]>([])
const fetching = ref(true)
const parentsOnly = ref(localStorage.getItem('_lastMenuRole') === 'parent')

const languages = ctx.state.languages.map(([value, text]) => ({ value, text }))
const productTypes = ctx.state.contentTypes.map(([value, text]) => ({ value, text }))
const coursesList = ctx.state.courses.map(([value, text]) => ({ value, text }))
const holidaysList = ctx.state.holiday.map(([value, text]) => ({ value, text }))

const canLoadMore = computed(() => {
  return (page.value - 1) * itemsOnPage.value + items.value.length < total.value
})

const showFeaturedProducts = computed(() => {
  const filters = { ...filtersData.value, sort_order: '' }

  return Object.values(filters).every(value => {
    if (Array.isArray(value)) return value.length === 0
    if (typeof value === 'boolean') return !value
    return !value
  })
})

const showParentAlert = computed(() => {
  return parentsOnly.value && (currentUser.hasTeacherPurchase || currentUser.id === 0)
})

let abortController: AbortController | null = null

function buildProductsRequestData(sliceFrom: number, sliceTo: number): FormData {
  const body = new FormData()

  body.append('slice_from', String(sliceFrom))
  body.append('slice_to', String(sliceTo))
  body.append('sort_order', filtersData.value.sort_order)
  body.append('parents_only', String(parentsOnly.value))

  if (filtersData.value.search_text.trim()) body.append('search_text', filtersData.value.search_text.trim())
  if (filtersData.value.min_price.trim()) body.append('min_price', filtersData.value.min_price.trim())
  if (filtersData.value.max_price.trim()) body.append('max_price', filtersData.value.max_price.trim())
  if (filtersData.value.language) body.append('language', filtersData.value.language)
  if (filtersData.value.owned) body.append('owned', 'true')
  if (filtersData.value.authored) body.append('authored', 'true')
  if (filtersData.value.available) body.append('available', 'true')

  for (const courseId of filtersData.value.courses) body.append('courses', String(courseId))
  for (const holidayId of filtersData.value.holiday) body.append('holiday', String(holidayId))
  for (const contentTypeId of filtersData.value.content_types) body.append('content_types', String(contentTypeId))

  return body
}

const refreshDelayed = debounce(async (scrollToTop: boolean) => {
  try {
    loadMoreCount.value = 0
    abortController = new AbortController()

    const sliceFrom = (page.value - 1) * itemsOnPage.value
    const sliceTo = sliceFrom + itemsOnPage.value
    const response = await apiPost<MarketProductCardItem[]>(
      ctx.urls.request_products,
      buildProductsRequestData(sliceFrom, sliceTo),
      { signal: abortController.signal }
    )

    if (response.failed) throw response

    items.value = response.data
    total.value = response.count ?? 0

    if (scrollToTop) window.scrollTo(0, 0)
  } catch (error) {
    const isAbortError = error instanceof DOMException && error.name === 'AbortError'
    if (!isAbortError) {
      console.error(error)
      items.value = []
      total.value = 0
    }
  } finally {
    fetching.value = false
  }
}, 300)

function refresh({ scroll = false }: { scroll?: boolean } = {}) {
  fetching.value = true
  abortController?.abort()
  refreshDelayed(scroll)
}

async function loadMore() {
  if (!canLoadMore.value) return

  loadMoreCount.value += 1

  const sliceFrom = (page.value + loadMoreCount.value - 1) * itemsOnPage.value
  const sliceTo = sliceFrom + itemsOnPage.value
  const response = await apiPost<MarketProductCardItem[]>(
    ctx.urls.request_products,
    buildProductsRequestData(sliceFrom, sliceTo)
  )

  if (response.failed) {
    console.error(response)
    return
  }

  items.value.push(...response.data)
  total.value = response.count ?? 0
}

function goToTeacherMarket() {
  window.history.pushState({ role: 'teacher' }, '')
  localStorage.setItem('_lastMenuRole', 'teacher')
  window.location.reload()
}

function onPopState(event: PopStateEvent) {
  const state = event.state as { role?: string } | null
  if (!state?.role) return

  localStorage.setItem('_lastMenuRole', state.role)
  window.location.reload()
}

function onFiltersChanged(value: MarketFiltersData) {
  filtersData.value = value
}

watch(itemsOnPage, (value, oldValue) => {
  refresh({ scroll: value < oldValue })
})

watch(page, () => {
  refresh()
})

watch(filtersData, () => {
  if (page.value === 1) {
    refresh()
    return
  }

  page.value = 1
})

onMounted(() => {
  const role = localStorage.getItem('_lastMenuRole')
  window.history.replaceState({ role }, '')
  parentsOnly.value = role === 'parent'
  window.addEventListener('popstate', onPopState)
  refresh()
})

onBeforeUnmount(() => {
  abortController?.abort()
  refreshDelayed.cancel()
  window.removeEventListener('popstate', onPopState)
})
</script>

<template>
  <div class="account-frame">
    <AppNavigation role="teacher" rules-url="/website/documents/oferta" />

    <main v-cloak id="list-products" class="content list-products-page">
      <header class="header">
        <h1 class="header__title">Ярмарка мастеров</h1>

        <v-button v-if="isMobile" class="header__add-product" mode="small flat primary" :href="ctx.urls.add_product">
          <v-icon class="mr-half">mdi-plus-circle</v-icon>
          Товар
        </v-button>

        <div class="header__links">
          <v-link class="header__link" mode="primary" :href="ctx.urls.my_products"> Мои товары </v-link>
        </div>

        <v-button v-if="!isMobile" class="header__add-product" mode="primary" :href="ctx.urls.add_product">
          <v-icon class="mr-half">mdi-plus-circle</v-icon>
          Добавить товар
        </v-button>
      </header>

      <div v-if="showParentAlert" class="parent-alert mb-4">
        Вы находитесь в списке товаров для родителей. Выбор ограничен.
        <v-link mode="primary" class="text-body-bold" @click="goToTeacherMarket">
          Перейти в список для учителей
        </v-link>
      </div>

      <MarketFiltersMobile
        v-if="isMobile"
        :languages-list="languages"
        :courses-list="coursesList"
        :product-types-list="productTypes"
        :holidays-list="holidaysList"
        @filters-changed="onFiltersChanged"
      />

      <MarketFiltersDesktop
        v-else
        :languages-list="languages"
        :courses-list="coursesList"
        :product-types-list="productTypes"
        :holidays-list="holidaysList"
        @filters-changed="onFiltersChanged"
      />

      <MarketFeaturedProducts :show="showFeaturedProducts" :parents-only="parentsOnly" class="mt-3" />

      <div class="products list-products-page__products mt-3">
        <ProductCard v-for="item in items" :key="item.id" :product="item" class="products__product" />

        <div v-if="fetching" class="products__empty d-flex align-center blue-text">
          <v-icon class="mdi-spin mr-1">mdi-timer-sand</v-icon>
          <p>Загрузка...</p>
        </div>

        <div v-else-if="items.length === 0" class="products__empty">
          <p>Ничего не найдено.</p>
        </div>
      </div>

      <div class="pagination mt-6">
        <v-button v-if="canLoadMore" mode="outlined block large" class="mb-2" @click="loadMore">
          Показать ещё
        </v-button>

        <v-pagination v-model="page" :total="total" show-next :items-on-page="itemsOnPage" />

        <v-select
          v-if="!isMobile"
          v-model="itemsOnPage"
          :items="paginationItems"
          class="pagination__show-by ml-auto"
          @update:model-value="page = 1"
        >
          <template #prepend>
            <span class="text-nowrap mr-half">Показывать по</span>
          </template>
        </v-select>
      </div>
    </main>
  </div>
</template>

<style lang="scss">
@use '@/styles/pages/market/list_products.scss' as *;
</style>
